// =========================================================
// === AI STRATEGY CONFIGURATION (MATCHING NEW JSON SCHEMA) ===
// =========================================================

// --- 1. TYPES ---
export interface AiOption {
    label: string;
    intent: string; // Maps to "id" in your JSON
    icon: string;
    description?: string;
}

// --- 2. HELPERS ---
const fmtCur = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);
const fmtNum = (n: number) => new Intl.NumberFormat('id-ID').format(n);

// --- 3. OPTIONS FACTORY (Mapped to your JSON) ---
export function getAiOptions(type: string): AiOption[] {
    
    // 1. Omzet Trend
    if (type === 'omzet_trend') return [
        { label: "Trend Summary", intent: "revenue_trend_summary", icon: "📊", description: "Up, Down, or Stable?" },
        { label: "Volatility Check", intent: "revenue_volatility", icon: "📉", description: "Stable vs Unstable" },
        { label: "Recent vs Past", intent: "recent_performance", icon: "🗓️", description: "Last week performance" }
    ];

    // 2. Transaction & APC
    if (type === 'tc_apc_trend') return [
        { label: "Traffic vs Spend", intent: "traffic_vs_spend", icon: "⚖️", description: "Which drives revenue?" },
        { label: "Traffic Stability", intent: "transaction_stability", icon: "👥", description: "Consistent footfall?" },
        { label: "APC Direction", intent: "apc_direction", icon: "💳", description: "Spending trend" }
    ];

    // 3. Top 5 Products (Chart)
    if (type === 'top_products') return [
        { label: "Contribution", intent: "top_product_contribution", icon: "🏆", description: "Rev concentration" },
        { label: "Revenue Gap", intent: "revenue_gap", icon: "↔️", description: "Leader vs Rest" },
        { label: "Ranking Stability", intent: "ranking_stability", icon: "🛡️", description: "Clear winner?" }
    ];

    // 4. Top 10 Qty (Table)
    if (type === 'top_10_qty') return [
        { label: "High Vol Items", intent: "high_volume_products", icon: "📦", description: "Freq sellers" },
        { label: "Vol Concentration", intent: "volume_concentration", icon: "🎯", description: "Spread or Focused?" },
        { label: "Ops Load", intent: "operational_load", icon: "⚙️", description: "Kitchen stress" }
    ];

    // 5. Top 10 Rev (Table)
    if (type === 'top_10_rev') return [
        { label: "Rev Drivers", intent: "revenue_drivers", icon: "💰", description: "Main contributors" },
        { label: "Dependency Risk", intent: "revenue_dependency", icon: "⚠️", description: "Reliance check" },
        { label: "Rev Distribution", intent: "revenue_distribution", icon: "🍰", description: "Spread analysis" }
    ];

    // 6. Channel Sales
    if (type === 'channel_sales') return [
        { label: "Channel Mix", intent: "channel_contribution", icon: "🛵", description: "Top contributors" },
        { label: "Dependency", intent: "channel_dependency", icon: "🔗", description: "Single source risk" },
        { label: "Balance Check", intent: "channel_balance", icon: "⚖️", description: "Even spread?" }
    ];

    // 7. Category Breakdown
    if (type === 'category_sales') return [
        { label: "Top Categories", intent: "category_leaders", icon: "🥇", description: "Best performers" },
        { label: "Category Gap", intent: "category_gap", icon: "📏", description: "Lead margin" },
        { label: "Concentration", intent: "category_concentration", icon: "🎯", description: "Diversity check" }
    ];

    // 8. Hourly Heatmap
    if (type === 'heatmap_hourly') return [
        { label: "Peak Hours", intent: "peak_hours", icon: "🔥", description: "Busiest times" },
        { label: "Slow Hours", intent: "low_hours", icon: "💤", description: "Dead time insight" },
        { label: "Timing Pattern", intent: "hourly_distribution", icon: "⏰", description: "Sales rhythm" }
    ];

    // 9. Daily Heatmap
    if (type === 'heatmap_daily') return [
        { label: "Best/Worst Days", intent: "best_worst_days", icon: "📅", description: "Weekday winners" },
        { label: "Weekly Pattern", intent: "weekly_pattern", icon: "🔄", description: "Recurring trends" },
        { label: "Day Distro", intent: "daily_distribution", icon: "📊", description: "Revenue spread" }
    ];

    // 10. P&L Overview
    if (type === 'pnl_overview') return [
        { label: "Profit Structure", intent: "profit_structure", icon: "🏗️", description: "Rev to Profit flow" },
        { label: "Cost Balance", intent: "cost_balance", icon: "⚖️", description: "Cost absorption" },
        { label: "Health Check", intent: "profit_health", icon: "❤️", description: "Overall verdict" }
    ];

    // 11. Net Profit Margin
    if (type === 'net_profit_margin') return [
        { label: "Margin Meaning", intent: "margin_interpretation", icon: "📝", description: "Good or bad?" },
        { label: "Cost Pressure", intent: "cost_pressure", icon: "🗜️", description: "Profit squeeze" },
        { label: "Efficiency", intent: "profit_efficiency", icon: "⚡", description: "Conversion rate" }
    ];

    // 12. COGS Ratio
    if (type === 'cogs_ratio') return [
        { label: "COGS %", intent: "cogs_proportion", icon: "🍳", description: "Food cost share" },
        { label: "Gross Margin", intent: "gross_profit_impact", icon: "📉", description: "Impact on GP" },
        { label: "Intensity", intent: "cogs_intensity", icon: "🔥", description: "High vs Low" }
    ];

    // 13. OPEX Ratio
    if (type === 'operational_ratio') return [
        { label: "OPEX %", intent: "opex_share", icon: "🏢", description: "Overhead share" },
        { label: "Pressure", intent: "overhead_pressure", icon: "😰", description: "Heavy load?" },
        { label: "Efficiency", intent: "operational_efficiency", icon: "🚀", description: "Vs Standard" }
    ];

    // Default Fallback
    return [
        { label: "General Analysis", intent: "general_review", icon: "📊", description: "Overview" },
        { label: "Identify Risks", intent: "risk_check", icon: "⚠️", description: "Warnings" },
        { label: "Growth Ideas", intent: "growth_strategy", icon: "🚀", description: "Opportunities" }
    ];
}

// --- 4. DATA SERIALIZERS (Helper functions to format data for AI) ---

function serializeTrendData(data: any[], valueKey: string): string {
    if (!data || data.length === 0) return "No trend data available.";
    const points = data.slice(0, 31).map(d => {
        const date = new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        const val = d[valueKey] || 0;
        return `${date}: ${fmtCur(val)}`;
    });
    return points.join(', ');
}

function serializeProductData(data: any[], type: 'qty' | 'rev'): string {
    const map = new Map<string, number>();
    data.forEach(s => {
        const source = type === 'rev' ? s.menuItemRevenues : s.menuItemQuantities;
        if (source) Object.values(source).forEach((c: any) => Object.entries(c).forEach(([k, v]) => map.set(k, (map.get(k) || 0) + Number(v))));
    });
    const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
    return sorted.map((p, i) => `${i+1}. ${p[0]} (${type === 'rev' ? fmtCur(p[1]) : fmtNum(p[1])})`).join('\n');
}

function serializeCategoryData(data: any[]): string {
    const map = new Map<string, number>();
    data.forEach(s => {
        if (s.categoryRevenues) {
            Object.entries(s.categoryRevenues).forEach(([k, v]) => map.set(k, (map.get(k) || 0) + Number(v)));
        }
    });
    const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    return sorted.map(p => `${p[0]}: ${fmtCur(p[1])}`).join(', ');
}

function serializeHourlyData(data: any[]): string {
    const hourlyTotal = new Array(24).fill(0);
    data.forEach(s => {
        if (s.hourlySales) {
            Object.entries(s.hourlySales).forEach(([h, v]) => {
                const hour = parseInt(h);
                if (!isNaN(hour) && hour >= 0 && hour < 24) {
                    hourlyTotal[hour] += Number(v);
                }
            });
        }
    });
    const withHour = hourlyTotal.map((val, h) => ({ hour: h, val }));
    const sorted = [...withHour].sort((a, b) => b.val - a.val);
    const top3 = sorted.slice(0, 3).map(x => `${x.hour}:00 (${fmtCur(x.val)})`).join(', ');
    const bottom3 = sorted.filter(x => x.val > 0).slice(-3).map(x => `${x.hour}:00`).join(', ');
    return `Top Hours: ${top3}. Slowest Active Hours: ${bottom3}.`;
}

function serializeDailyPattern(data: any[]): string {
    const dayTotals = new Map<string, { sum: number, count: number }>();
    data.forEach(s => {
        const date = new Date(s.date);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        const current = dayTotals.get(dayName) || { sum: 0, count: 0 };
        dayTotals.set(dayName, { sum: current.sum + s.totalOmzet, count: current.count + 1 });
    });
    const averages = Array.from(dayTotals.entries())
        .map(([day, stats]) => ({ day, avg: stats.sum / stats.count }))
        .sort((a, b) => b.avg - a.avg);
    return averages.map(x => `${x.day}: ${fmtCur(x.avg)}`).join(', ');
}


// --- 5. PROMPT FACTORY (Mapped to JSON Instructions) ---
export function buildAiPrompt(params: {
    blockType: string;
    intent: string;
    branchName: string;
    period: string;
    salesData: any[];
    pnlData?: any;
    chartTitle: string;
}): string {
    const { blockType, intent, branchName, period, salesData, pnlData, chartTitle } = params;

    let instruction = "";

    switch (intent) {
        // 1. Omzet Trend
        case 'revenue_trend_summary': instruction = "Explain whether revenue is trending up, down, or stable over the last period. Use specific dates and numbers from the trend data."; break;
        case 'revenue_volatility': instruction = "Highlight how stable daily revenue is. Identify any unusually high or low sales days and speculate on potential causes (e.g. weekends)."; break;
        case 'recent_performance': instruction = "Compare the most recent week of data against earlier weeks to show short-term performance momentum."; break;

        // 2. Transaction & APC
        case 'traffic_vs_spend': instruction = "Explain whether revenue is being driven more by customer volume (Visits) or average spending (APC). Which lever is pulling harder?"; break;
        case 'transaction_stability': instruction = "Identify whether daily transaction counts are consistent or highly fluctuating. Are there days with worryingly low footfall?"; break;
        case 'apc_direction': instruction = "Highlight whether customers are spending more or less per visit over time. Is the trend positive or negative?"; break;

        // 3. Top 5 Products
        case 'top_product_contribution': instruction = "Explain how much total revenue is concentrated in the top few products. Is the business relying heavily on its stars?"; break;
        case 'revenue_gap': instruction = "Highlight the revenue gap between the #1 product and the rest. Is there a dominant leader?"; break;
        case 'ranking_stability': instruction = "Identify whether the top products clearly dominate the menu or if competition among top items is close."; break;

        // 4. Top 10 Qty
        case 'high_volume_products': instruction = "Identify products that sell frequently regardless of revenue size. These are the 'Workhorse' items."; break;
        case 'volume_concentration': instruction = "Explain whether sales volume is spread out across the menu or dominated by a few specific items."; break;
        case 'operational_load': instruction = "Highlight products that may be increasing operational workload/kitchen stress due to high sales volume."; break;

        // 5. Top 10 Rev
        case 'revenue_drivers': instruction = "Identify the products contributing most to total revenue (The 'Cash Cows')."; break;
        case 'revenue_dependency': instruction = "Explain the risk of relying on this small number of products for the bulk of revenue."; break;
        case 'revenue_distribution': instruction = "Show how evenly (or unevenly) revenue is spread across these top products."; break;

        // 6. Channel Sales
        case 'channel_contribution': instruction = "Explain which sales channels (e.g., Dine-in vs Delivery) contribute most to total revenue."; break;
        case 'channel_dependency': instruction = "Identify whether revenue relies heavily on a single channel. Is this a risk?"; break;
        case 'channel_balance': instruction = "Assess how evenly sales are distributed across channels. Is the mix healthy?"; break;

        // 7. Category Breakdown
        case 'category_leaders': instruction = "Identify the top-performing categories by revenue. What are the pillars of the menu?"; break;
        case 'category_gap': instruction = "Explain the revenue difference between leading and trailing categories."; break;
        case 'category_concentration': instruction = "Assess whether sales are diversified or focused on just one or two main categories."; break;

        // 8. Hourly Heatmap
        case 'peak_hours': instruction = "Highlight the busiest hours contributing most to revenue. Are these lunch/dinner peaks?"; break;
        case 'low_hours': instruction = "Identify consistently slower operating hours (Dead Zones)."; break;
        case 'hourly_distribution': instruction = "Explain how sales are distributed across the day. Is it a lunch business, dinner business, or all-day?"; break;

        // 9. Daily Heatmap
        case 'best_worst_days': instruction = "Identify the strongest and weakest weekdays based on average revenue."; break;
        case 'weekly_pattern': instruction = "Explain the recurring weekly pattern (e.g. Weekend spikes vs Weekday lulls)."; break;
        case 'daily_distribution': instruction = "Assess how evenly revenue is spread across the week. Is the business 'Weekend Dependent'?"; break;

        // 10. P&L Overview
        case 'profit_structure': instruction = "Explain how the Revenue flows into Costs and finally into Net Profit. Visualize the funnel."; break;
        case 'cost_balance': instruction = "Highlight how much of the revenue is being absorbed by costs (COGS + OPEX)."; break;
        case 'profit_health': instruction = "Assess whether the business is operating profitably and sustainably based on the Net Profit."; break;

        // 11. Net Profit Margin
        case 'margin_interpretation': instruction = "Explain what the current net profit margin % means. Is it healthy for F&B (typically 15-20%)?"; break;
        case 'cost_pressure': instruction = "Highlight whether costs are exerting significant pressure on profitability."; break;
        case 'profit_efficiency': instruction = "Assess how efficiently the business converts every $1 of revenue into profit."; break;

        // 12. COGS Ratio
        case 'cogs_proportion': instruction = "Explain how much revenue is spent specifically on producing goods (Food Cost)."; break;
        case 'gross_profit_impact': instruction = "Show how the current COGS level impacts Gross Profit availability."; break;
        case 'cogs_intensity': instruction = "Assess whether COGS takes a surprisingly large share of revenue (High Intensity)."; break;

        // 13. OPEX Ratio
        case 'opex_share': instruction = "Explain how much revenue goes to operating expenses (Rent, Utilities, Labor)."; break;
        case 'overhead_pressure': instruction = "Identify whether operating costs are relatively heavy/burdensome."; break;
        case 'operational_efficiency': instruction = "Assess operational efficiency. Is the OPEX spending justified by the revenue?"; break;

        // Default
        default: instruction = "Provide a general strategic summary of this data."; break;
    }

    // --- B. Build Data Context ---
    let dataContext = "";
    
    if (blockType === 'omzet_trend') {
        const total = salesData.reduce((s, d) => s + d.totalOmzet, 0);
        const dailyPoints = serializeTrendData(salesData, 'totalOmzet');
        dataContext = `Total Revenue: ${fmtCur(total)}.\nDaily Trend: [${dailyPoints}]`;
    } 
    else if (blockType === 'tc_apc_trend') {
        const tc = salesData.reduce((s, d) => s + d.totalTransactions, 0);
        const apc = tc > 0 ? salesData.reduce((s, d) => s + d.totalOmzet, 0) / tc : 0;
        const dailyTC = serializeTrendData(salesData, 'totalTransactions');
        dataContext = `Total Visits: ${fmtNum(tc)}, Avg Spend: ${fmtCur(apc)}.\nDaily Visits: [${dailyTC}]`;
    } 
    else if (blockType === 'top_products') {
        const productList = serializeProductData(salesData, 'rev');
        dataContext = `Top Products Data:\n${productList}`;
    } 
    else if (blockType === 'top_10_qty') {
        const productList = serializeProductData(salesData, 'qty');
        dataContext = `Top Products by Quantity:\n${productList}`;
    }
    else if (blockType === 'top_10_rev') {
        const productList = serializeProductData(salesData, 'rev');
        dataContext = `Top Products by Revenue:\n${productList}`;
    }
    else if (blockType === 'channel_sales') {
        const map = new Map<string, number>();
        salesData.forEach(s => {
            if(s.revenueByVisitPurpose) Object.entries(s.revenueByVisitPurpose).forEach(([k, v]) => map.set(k, (map.get(k) || 0) + Number(v)));
        });
        const channels = Array.from(map.entries()).map(e => `${e[0]}: ${fmtCur(e[1])}`).join(', ');
        dataContext = `Channel Sales Breakdown: ${channels}`;
    }
    else if (blockType === 'category_sales') {
        const categories = serializeCategoryData(salesData);
        dataContext = `Category Performance (Ranked by Rev): ${categories}`;
    }
    else if (blockType === 'heatmap_hourly') {
        const hourlyStats = serializeHourlyData(salesData);
        dataContext = `Hourly Sales Patterns:\n${hourlyStats}`;
    }
    else if (blockType === 'heatmap_daily') {
        const dailyStats = serializeDailyPattern(salesData);
        dataContext = `Daily Performance (Avg per Weekday): ${dailyStats}`;
    }
    else if (blockType.includes('pnl') || blockType.includes('profit') || blockType.includes('ratio')) {
        const rev = pnlData?.totalRevenue || 0;
        const net = pnlData?.netProfit || 0;
        const cogs = pnlData?.cogs || 0; 
        const opex = pnlData?.opex || 0; 
        dataContext = `
        Financial Data (P&L):
        - Revenue: ${fmtCur(rev)}
        - COGS: ${fmtCur(cogs)}
        - OPEX: ${fmtCur(opex)}
        - Net Profit: ${fmtCur(net)}
        `;
    } 
    else {
        dataContext = `Chart Title: ${chartTitle}`;
    }

    // --- C. Final Prompt Assembly ---
    return `
Role: Senior F&B Strategy Consultant.
Context: Branch "${branchName}", Period ${period}.

DATA:
${dataContext}

TASK:
${instruction}

STRICT OUTPUT RULES:
1. Max 150 words.
2. Format: Markdown.
3. Use "### " for section headers (Observation, Analysis, Action).
4. Tone: Direct, Professional, Insightful.

Structure:

### Observation
[What the specific numbers say]

### Analysis
[Why this implies the insight requested]

### Action
[Strategic recommendation]
`;
}